import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };
const heading = { fontFamily: "'Cormorant Garamond', serif" };

const STAGE_LABELS = {
  evidence_lock_review: 'Evidence Lock Review',
  doc_completeness: 'Document Review',
  presentation_prep: 'Presentation Prep',
  report_review: 'Report Review',
  docs_pending: 'Documents Pending',
  intake_pending: 'Intake Pending',
  clarity_call_pending: 'Clarity Call Pending',
  business_profile_review_pending: 'Business Profile Review',
  sentry_running: 'SENTRY Running',
  presentation_1_pending: 'Presentation 1 Pending',
  presentation_2_pending: 'Presentation 2 Pending',
  final_report_ready: 'Final Report Ready',
};

const STATUS_COLORS = {
  unassigned: { bg: 'rgba(184,144,68,0.12)', fg: '#B89044' },
  assigned: { bg: 'rgba(122,82,48,0.12)', fg: '#7A5230' },
  in_progress: { bg: 'rgba(42,95,63,0.12)', fg: '#2A5F3F' },
  blocked: { bg: 'rgba(122,31,46,0.12)', fg: '#7A1F2E' },
  complete: { bg: 'rgba(42,95,63,0.18)', fg: '#1E7A3F' },
};

const STATUS_OPTIONS = ['unassigned', 'assigned', 'in_progress', 'blocked', 'complete'];
const PRIORITY_OPTIONS = ['normal', 'high', 'urgent'];
const FILTER_TABS = ['ALL', 'UNASSIGNED', 'MINE', 'BLOCKED'];

function timeAgo(ts) {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr`;
  return `${Math.floor(diff / 86400)} days`;
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.unassigned;
  return (
    <span style={{
      ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
      padding: '3px 8px', background: c.bg, color: c.fg, display: 'inline-block',
    }}>
      {(status || 'unassigned').replace(/_/g, ' ')}
    </span>
  );
}

function PriorityDot({ priority }) {
  const color = priority === 'urgent' ? '#7A1F2E' : priority === 'high' ? '#B85A2E' : 'transparent';
  if (color === 'transparent') return null;
  return <span style={{ display: 'inline-block', width: 6, height: 6, background: color, marginRight: 6 }} />;
}

export default function Cases() {
  const [cases, setCases] = useState([]);
  const [engagements, setEngagements] = useState({});
  const [notes, setNotes] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [userName, setUserName] = useState(() => localStorage.getItem('cordis_user_name') || '');
  const [showNameInput, setShowNameInput] = useState(false);
  const [assignName, setAssignName] = useState('');
  const [showAssignInput, setShowAssignInput] = useState(false);

  const loadCases = useCallback(async () => {
    const { data, error } = await supabase
      .from('cordis_cases')
      .select('*')
      .order('status', { ascending: true })
      .order('updated_at', { ascending: false });
    if (!error && data) {
      // Sort: unassigned first, then by updated_at desc
      const sorted = [...data].sort((a, b) => {
        if (a.status === 'unassigned' && b.status !== 'unassigned') return -1;
        if (a.status !== 'unassigned' && b.status === 'unassigned') return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      setCases(sorted);

      // Load engagements for all unique engagement_ids
      const engIds = [...new Set(data.map(c => c.engagement_id).filter(Boolean))];
      if (engIds.length > 0) {
        const { data: engs } = await supabase
          .from('cordis_engagements')
          .select('id, client_name, company')
          .in('id', engIds);
        if (engs) {
          const map = {};
          engs.forEach(e => { map[e.id] = e; });
          setEngagements(map);
        }
      }

      // Load latest note for each case
      const noteMap = {};
      for (const c of data) {
        const { data: caseNotes } = await supabase
          .from('cordis_case_notes')
          .select('*')
          .eq('case_id', c.id)
          .order('created_at', { ascending: false })
          .limit(1);
        if (caseNotes && caseNotes.length > 0) {
          noteMap[c.id] = caseNotes[0];
        }
      }
      setNotes(noteMap);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadCases(); }, [loadCases]);

  const selected = cases.find(c => c.id === selectedId);
  const eng = selected ? engagements[selected.engagement_id] : null;

  const filtered = cases.filter(c => {
    if (filter === 'UNASSIGNED') return c.status === 'unassigned';
    if (filter === 'MINE') return c.assigned_to && c.assigned_to === userName;
    if (filter === 'BLOCKED') return c.status === 'blocked';
    return true;
  });

  const unassignedCount = cases.filter(c => c.status === 'unassigned').length;

  // Detail: notes for selected case
  const [detailNotes, setDetailNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const loadDetailNotes = useCallback(async (caseId) => {
    if (!caseId) return;
    setLoadingNotes(true);
    const { data } = await supabase
      .from('cordis_case_notes')
      .select('*')
      .eq('case_id', caseId)
      .order('created_at', { ascending: true });
    setDetailNotes(data || []);
    setLoadingNotes(false);
  }, []);

  useEffect(() => {
    if (selectedId) loadDetailNotes(selectedId);
  }, [selectedId, loadDetailNotes]);

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    await supabase
      .from('cordis_cases')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', selected.id);
    loadCases();
  };

  const handleAssign = async () => {
    if (!selected || !assignName.trim()) return;
    await supabase
      .from('cordis_cases')
      .update({
        assigned_to: assignName.trim(),
        assigned_at: new Date().toISOString(),
        status: 'assigned',
        updated_at: new Date().toISOString(),
      })
      .eq('id', selected.id);
    setShowAssignInput(false);
    setAssignName('');
    loadCases();
  };

  const handlePriorityChange = async (newPriority) => {
    if (!selected) return;
    await supabase
      .from('cordis_cases')
      .update({ priority: newPriority, updated_at: new Date().toISOString() })
      .eq('id', selected.id);
    loadCases();
  };

  const handlePostNote = async () => {
    if (!selected || !newNote.trim()) return;
    if (!userName) {
      setShowNameInput(true);
      return;
    }
    setPostingNote(true);
    const { data } = await supabase
      .from('cordis_case_notes')
      .insert({ case_id: selected.id, author: userName, note: newNote.trim() })
      .select();
    if (data && data.length > 0) {
      setDetailNotes(prev => [...prev, data[0]]);
      setNewNote('');
      // Update latest note in queue preview
      setNotes(prev => ({ ...prev, [selected.id]: data[0] }));
    }
    setPostingNote(false);
  };

  const handleSetName = () => {
    if (!userName.trim()) return;
    localStorage.setItem('cordis_user_name', userName.trim());
    setShowNameInput(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#7A94A8' }}>
        <span style={{ ...mono, fontSize: 11, letterSpacing: '0.14em' }}>LOADING CASES...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#FAFAF8' }}>
      <style>{`
        @media (max-width: 768px) {
          .cases-layout { flex-direction: column !important; }
          .cases-queue { width: 100% !important; max-height: 50vh; border-right: none !important; border-bottom: 1px solid rgba(7,25,42,0.08); }
          .cases-detail { width: 100% !important; }
        }
      `}</style>

      {/* LEFT: Case Queue */}
      <div className="cases-layout cases-queue" style={{
        width: '35%', borderRight: '1px solid rgba(7,25,42,0.08)',
        overflowY: 'auto', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '28px 24px 16px', borderBottom: '1px solid rgba(7,25,42,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <h1 style={{ ...heading, fontSize: 28, fontWeight: 300, color: '#0A1828', margin: 0 }}>Cases</h1>
            {unassignedCount > 0 && (
              <span style={{
                ...mono, fontSize: 9, padding: '2px 8px',
                background: 'rgba(184,144,68,0.15)', color: '#B89044',
              }}>
                {unassignedCount}
              </span>
            )}
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 0, marginTop: 16 }}>
            {FILTER_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                style={{
                  ...mono, fontSize: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
                  padding: '8px 12px', border: 'none', cursor: 'pointer',
                  background: filter === tab ? '#0A1828' : 'transparent',
                  color: filter === tab ? '#FFFFFF' : '#7A94A8',
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Case list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', ...mono, fontSize: 10, color: '#7A94A8' }}>
              No cases
            </div>
          )}
          {filtered.map(c => {
            const cEng = engagements[c.engagement_id];
            const latestNote = notes[c.id];
            const isSelected = selectedId === c.id;
            return (
              <div
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                style={{
                  padding: '16px 24px', cursor: 'pointer',
                  borderBottom: '1px solid rgba(7,25,42,0.06)',
                  background: isSelected ? 'rgba(122,82,48,0.06)' : 'transparent',
                  borderLeft: isSelected ? '3px solid #7A5230' : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <PriorityDot priority={c.priority} />
                    <span style={{ ...sans, fontSize: 14, color: '#0A1828', fontWeight: 500 }}>
                      {cEng ? cEng.client_name : 'Unknown'}
                    </span>
                    {cEng && cEng.company && (
                      <span style={{ ...sans, fontSize: 12, color: '#7A94A8', marginLeft: 8 }}>{cEng.company}</span>
                    )}
                  </div>
                  <span style={{ ...mono, fontSize: 8, color: '#7A94A8', whiteSpace: 'nowrap' }}>
                    {timeAgo(c.created_at)}
                  </span>
                </div>

                <div style={{ ...mono, fontSize: 9, color: '#4A6278', marginBottom: 6 }}>
                  {STAGE_LABELS[c.stage] || c.stage}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <StatusBadge status={c.status} />
                  <span style={{ ...mono, fontSize: 8, color: c.assigned_to ? '#4A6278' : '#B89044' }}>
                    {c.assigned_to || 'Unassigned'}
                  </span>
                </div>

                {latestNote && (
                  <div style={{ ...sans, fontSize: 11, color: '#7A94A8', marginTop: 8, lineHeight: 1.4 }}>
                    {latestNote.note.length > 60 ? latestNote.note.slice(0, 60) + '...' : latestNote.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT: Case Detail */}
      <div className="cases-layout cases-detail" style={{ width: '65%', display: 'flex', flexDirection: 'column' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ ...mono, fontSize: 11, letterSpacing: '0.14em', color: '#7A94A8' }}>
              SELECT A CASE
            </span>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Detail header */}
            <div style={{ padding: '28px 32px 20px', borderBottom: '1px solid rgba(7,25,42,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ ...heading, fontSize: 24, fontWeight: 300, color: '#0A1828', margin: '0 0 4px' }}>
                    {eng ? eng.client_name : 'Unknown'}
                    {eng && eng.company && (
                      <span style={{ ...sans, fontSize: 14, color: '#7A94A8', marginLeft: 12, fontWeight: 400 }}>{eng.company}</span>
                    )}
                  </h2>
                  <div style={{ ...mono, fontSize: 10, color: '#7A5230', letterSpacing: '0.1em' }}>
                    {STAGE_LABELS[selected.stage] || selected.stage}
                  </div>
                </div>
                <a
                  href="/pipeline"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...mono, fontSize: 9, color: '#7A5230', textDecoration: 'none', letterSpacing: '0.1em' }}
                >
                  View Engagement
                </a>
              </div>

              {/* Controls row */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 20, flexWrap: 'wrap' }}>
                {/* Status dropdown */}
                <div>
                  <div style={{ ...mono, fontSize: 7, letterSpacing: '0.16em', color: '#7A94A8', marginBottom: 4 }}>STATUS</div>
                  <select
                    value={selected.status}
                    onChange={e => handleStatusChange(e.target.value)}
                    style={{
                      ...mono, fontSize: 10, padding: '8px 12px', border: '1px solid rgba(7,25,42,0.10)',
                      background: '#FFFFFF', color: '#0A1828', outline: 'none', cursor: 'pointer',
                    }}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                {/* Priority */}
                <div>
                  <div style={{ ...mono, fontSize: 7, letterSpacing: '0.16em', color: '#7A94A8', marginBottom: 4 }}>PRIORITY</div>
                  <div style={{ display: 'flex', gap: 0 }}>
                    {PRIORITY_OPTIONS.map(p => (
                      <button
                        key={p}
                        onClick={() => handlePriorityChange(p)}
                        style={{
                          ...mono, fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase',
                          padding: '8px 12px', border: '1px solid rgba(7,25,42,0.10)', cursor: 'pointer',
                          background: selected.priority === p ? '#0A1828' : '#FFFFFF',
                          color: selected.priority === p ? '#FFFFFF' : '#7A94A8',
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assign */}
                <div>
                  <div style={{ ...mono, fontSize: 7, letterSpacing: '0.16em', color: '#7A94A8', marginBottom: 4 }}>ASSIGNED TO</div>
                  {selected.assigned_to && !showAssignInput ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...sans, fontSize: 13, color: '#0A1828' }}>{selected.assigned_to}</span>
                      <button
                        onClick={() => { setShowAssignInput(true); setAssignName(selected.assigned_to || ''); }}
                        style={{ ...mono, fontSize: 8, color: '#7A5230', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        change
                      </button>
                    </div>
                  ) : showAssignInput ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input
                        value={assignName}
                        onChange={e => setAssignName(e.target.value)}
                        placeholder="Name"
                        style={{ ...sans, fontSize: 12, padding: '6px 10px', border: '1px solid rgba(7,25,42,0.10)', outline: 'none', width: 140 }}
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') handleAssign(); }}
                      />
                      <button
                        onClick={handleAssign}
                        style={{ ...mono, fontSize: 9, padding: '6px 12px', background: '#7A5230', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
                      >
                        Assign
                      </button>
                      <button
                        onClick={() => setShowAssignInput(false)}
                        style={{ ...mono, fontSize: 9, padding: '6px 8px', background: 'none', color: '#7A94A8', border: '1px solid rgba(7,25,42,0.10)', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setShowAssignInput(true); setAssignName(userName || ''); }}
                      style={{ ...mono, fontSize: 9, padding: '8px 14px', background: '#7A5230', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
                    >
                      Assign to me
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Notes thread */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 32px' }}>
              <div style={{ ...mono, fontSize: 8, letterSpacing: '0.16em', color: '#7A94A8', marginBottom: 16 }}>NOTES</div>

              {loadingNotes ? (
                <div style={{ ...mono, fontSize: 10, color: '#7A94A8' }}>Loading...</div>
              ) : detailNotes.length === 0 ? (
                <div style={{ ...sans, fontSize: 13, color: '#7A94A8', padding: '16px 0' }}>No notes yet</div>
              ) : (
                detailNotes.map(n => (
                  <div key={n.id} style={{
                    borderLeft: '2px solid rgba(122,82,48,0.3)', paddingLeft: 16,
                    marginBottom: 16,
                  }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ ...mono, fontSize: 9, letterSpacing: '0.1em', color: '#7A5230', textTransform: 'uppercase' }}>
                        {n.author}
                      </span>
                      <span style={{ ...mono, fontSize: 8, color: '#7A94A8' }}>
                        {new Date(n.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ ...sans, fontSize: 13, color: '#0A1828', lineHeight: 1.5 }}>
                      {n.note}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* New note input */}
            <div style={{ padding: '16px 32px 24px', borderTop: '1px solid rgba(7,25,42,0.08)' }}>
              {showNameInput && !userName && (
                <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ ...mono, fontSize: 8, color: '#7A94A8' }}>Your name:</div>
                  <input
                    value={userName}
                    onChange={e => setUserName(e.target.value)}
                    style={{ ...sans, fontSize: 12, padding: '6px 10px', border: '1px solid rgba(7,25,42,0.10)', outline: 'none', width: 160 }}
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleSetName(); }}
                  />
                  <button
                    onClick={handleSetName}
                    style={{ ...mono, fontSize: 9, padding: '6px 12px', background: '#7A5230', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
                  >
                    Set
                  </button>
                </div>
              )}
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Add a note..."
                rows={3}
                style={{
                  ...sans, fontSize: 13, padding: '12px 14px', width: '100%',
                  border: '1px solid rgba(7,25,42,0.10)', outline: 'none', resize: 'vertical',
                  background: '#FFFFFF', color: '#0A1828',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  onClick={handlePostNote}
                  disabled={postingNote || !newNote.trim()}
                  style={{
                    ...mono, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                    padding: '10px 20px', background: '#7A5230', color: '#FFFFFF', border: 'none',
                    cursor: postingNote || !newNote.trim() ? 'not-allowed' : 'pointer',
                    opacity: postingNote || !newNote.trim() ? 0.5 : 1,
                  }}
                >
                  {postingNote ? 'Posting...' : 'Post Note'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
